import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Mic, MicOff, Sparkles, MapPin, Calendar, ShoppingBag, Plane, TrendingUp, Users, Lightbulb, BarChart3 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';

const AIAssistant = () => {
  const { aiChatOpen, setAiChatOpen, aiMessages, setAiMessages, user, userMode } = useApp();
  const { t } = useI18n();
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Different quick actions for consumer vs partner
  const consumerQuickActions = [
    { icon: MapPin, text: 'Trova parrucchiere vicino', color: 'emerald' },
    { icon: Calendar, text: 'Prenota per domani', color: 'blue' },
    { icon: ShoppingBag, text: 'I miei ordini', color: 'purple' },
    { icon: Plane, text: 'Pianifica un viaggio', color: 'amber' }
  ];

  const partnerQuickActions = [
    { icon: TrendingUp, text: 'Come stanno andando le vendite?', color: 'emerald' },
    { icon: Users, text: 'Quali clienti tornano di più?', color: 'blue' },
    { icon: Lightbulb, text: 'Idee per aumentare le prenotazioni', color: 'amber' },
    { icon: BarChart3, text: 'Servizi più richiesti', color: 'purple' }
  ];

  const quickActions = userMode === 'partner' ? partnerQuickActions : consumerQuickActions;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  const handleSend = async () => {
    if (!input.trim()) return;
    
    const userMessage = { role: 'user', content: input };
    setAiMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Different responses for consumer vs partner
    setTimeout(() => {
      let responses;
      
      if (userMode === 'partner') {
        // Partner mode - business assistant responses
        responses = [
          `Ciao ${user.name.split(' ')[0]}! Analizziamo i tuoi dati. Questa settimana hai avuto un incremento del 15% nelle prenotazioni rispetto alla precedente. Il servizio più richiesto è stato "Taglio Uomo" con 23 prenotazioni.`,
          `Ottima domanda! Ho notato che i tuoi clienti ELIT prenotano in media 3 volte al mese. Ti consiglio di creare un'offerta speciale per fidelizzarli ulteriormente.`,
          `Basandomi sui tuoi dati, il martedì e il giovedì sono i giorni con meno prenotazioni. Potresti lanciare una promozione "Happy Hour" in quei giorni per aumentare il flusso.`,
          `I tuoi clienti più fedeli sono principalmente nella fascia 25-35 anni. Il 70% di loro ha scoperto la tua attività tramite Skipres. Vuoi vedere quali servizi preferiscono?`
        ];
      } else {
        // Consumer mode - marketplace assistant responses
        responses = [
          `Perfetto ${user.name.split(' ')[0]}! Ho trovato 12 attività disponibili per "${input}". Vuoi che ti mostri i risultati sulla mappa o preferisci una lista?`,
          `Ottima scelta! Ho individuato alcune opzioni interessanti. Posso prenotare per te o vuoi prima vedere i dettagli?`,
          `Certo! Sto cercando le migliori soluzioni per te. Ho notato che preferisci attività con rating alto - filtro i risultati di conseguenza.`
        ];
      }
      
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: responses[Math.floor(Math.random() * responses.length)]
      }]);
      setIsTyping(false);
    }, 1500);
  };

  const toggleVoice = () => {
    setIsListening(!isListening);
    if (!isListening) {
      setTimeout(() => {
        setInput('Trova un ristorante giapponese per stasera');
        setIsListening(false);
      }, 2000);
    }
  };

  if (!aiChatOpen) {
    // Floating button (visible on desktop only, mobile uses tab bar)
    return (
      <button
        onClick={() => setAiChatOpen(true)}
        className="hidden lg:flex fixed bottom-6 right-6 z-40 items-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
        data-testid="ai-assistant-floating-btn"
      >
        <Sparkles className="w-5 h-5" />
        <span className="font-medium">{t('header', 'aiAssistant')}</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[500] bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setAiChatOpen(false)}>
      {/* AI Chat Window - covers the tab bar, starts from bottom */}
      <div 
        className="absolute inset-x-0 bottom-0 md:inset-auto md:right-6 md:bottom-6 md:w-[420px] bg-white md:rounded-2xl rounded-t-3xl flex flex-col animate-slide-up shadow-2xl max-h-[80vh] md:max-h-[600px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {userMode === 'partner' ? 'Business Assistant' : 'Assistente Skipres'}
              </h3>
              <p className="text-xs text-emerald-500">
                {userMode === 'partner' ? 'Il tuo consulente aziendale' : 'Online • Pronto ad aiutarti'}
              </p>
            </div>
          </div>
          <button 
            onClick={() => setAiChatOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px]">
          {aiMessages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-3 rounded-2xl ${
                msg.role === 'user' 
                  ? 'bg-emerald-500 text-white rounded-br-md' 
                  : 'bg-gray-100 text-gray-800 rounded-bl-md'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-800 p-3 rounded-2xl rounded-bl-md">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {aiMessages.length <= 2 && (
          <div className="px-4 pb-2">
            <p className="text-xs text-gray-500 mb-2">Suggerimenti rapidi:</p>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {quickActions.map((action, i) => (
                <button
                  key={i}
                  onClick={() => setInput(action.text)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm whitespace-nowrap transition-colors"
                >
                  <action.icon className="w-4 h-4" />
                  {action.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-2 bg-gray-100 rounded-full p-2">
            <button 
              onClick={toggleVoice}
              className={`p-2 rounded-full transition-all duration-300 ${
                isListening 
                  ? 'bg-red-500 text-white animate-pulse' 
                  : 'hover:bg-gray-200 text-gray-500'
              }`}
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={isListening ? 'Sto ascoltando...' : 'Scrivi o parla...'}
              className="flex-1 bg-transparent outline-none text-gray-800 placeholder-gray-400"
            />
            <button 
              onClick={handleSend}
              disabled={!input.trim()}
              className="p-2 bg-emerald-500 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-emerald-600 transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
